const STORAGE_KEY = 'velostruct:published-links';
const MAX_LINKS = 100;

export interface PublishedLinkRecord {
  id: string;
  name: string;
  url: string;
  hash: string;
  checksum: string;
  payload: string;
  createdAt: number;
}

function loadRaw(): PublishedLinkRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is PublishedLinkRecord =>
      item &&
      typeof item.id === 'string' &&
      typeof item.name === 'string' &&
      typeof item.url === 'string' &&
      typeof item.hash === 'string' &&
      typeof item.checksum === 'string' &&
      typeof item.payload === 'string' &&
      typeof item.createdAt === 'number',
    );
  } catch {
    return [];
  }
}

function persist(records: PublishedLinkRecord[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records.slice(0, MAX_LINKS)));
  } catch {
    // Ignore quota/storage errors.
  }
}

export function loadPublishedLinks(): PublishedLinkRecord[] {
  return loadRaw().sort((a, b) => b.createdAt - a.createdAt);
}

export function addPublishedLink(
  input: Omit<PublishedLinkRecord, 'id' | 'createdAt'>,
): { links: PublishedLinkRecord[]; record: PublishedLinkRecord } {
  const existing = loadPublishedLinks();
  const duplicate = existing.find((item) => item.hash === input.hash);
  if (duplicate) {
    const refreshed: PublishedLinkRecord = {
      ...duplicate,
      ...input,
      createdAt: Date.now(),
    };
    const next = [refreshed, ...existing.filter((item) => item.id !== duplicate.id)];
    persist(next);
    return { links: next, record: refreshed };
  }

  const record: PublishedLinkRecord = {
    ...input,
    id: `pub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: Date.now(),
  };
  const next = [record, ...existing];
  persist(next);
  return { links: next, record };
}

export function removePublishedLink(id: string): PublishedLinkRecord[] {
  const next = loadPublishedLinks().filter((item) => item.id !== id);
  persist(next);
  return next;
}
